import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { teamsApi, usersApi, rolesApi, TeamRecord, UserRecord, RoleRecord } from '../services/creatorApi';
import { authSession, generateSalt, hashPassword, verifyPassword, generateToken } from '../services/authService';
// ── Permission system ──
export type Permission = 'forms.view' | 'forms.create' | 'forms.edit' | 'forms.delete' | 'templates.view' | 'templates.create' | 'templates.edit' | 'templates.delete' | 'lists.view' | 'lists.create' | 'lists.edit' | 'lists.delete' | 'lists.manage_intake' | 'campaigns.view' | 'campaigns.create' | 'campaigns.edit' | 'campaigns.send' | 'campaigns.delete' | 'submissions.view' | 'submissions.export' | 'subscriptions.view' | 'subscriptions.manage' | 'subscriptions.export' | 'contacts.view' | 'contacts.create' | 'contacts.edit' | 'contacts.delete' | 'contacts.export' | 'tags.view' | 'tags.create' | 'tags.edit' | 'tags.delete' | 'configs.view' | 'configs.create' | 'configs.edit' | 'configs.delete' | 'users.view' | 'users.invite' | 'users.edit' | 'users.delete' | 'users.manage_roles' | 'settings.view' | 'settings.edit' | 'audit.view' | 'audit.export' | 'roles.view' | 'roles.create' | 'roles.edit' | 'roles.delete';
export const permissionCategories: Record<string, string[]> = {
  'Signup Forms': ['forms.view', 'forms.create', 'forms.edit', 'forms.delete'],
  'Email Templates': ['templates.view', 'templates.create', 'templates.edit', 'templates.delete'],
  'Mailing Lists': ['lists.view', 'lists.create', 'lists.edit', 'lists.delete', 'lists.manage_intake'],
  Campaigns: ['campaigns.view', 'campaigns.create', 'campaigns.edit', 'campaigns.send', 'campaigns.delete'],
  Submissions: ['submissions.view', 'submissions.export'],
  Subscriptions: ['subscriptions.view', 'subscriptions.manage', 'subscriptions.export'],
  Contacts: ['contacts.view', 'contacts.create', 'contacts.edit', 'contacts.delete', 'contacts.export'],
  Tags: ['tags.view', 'tags.create', 'tags.edit', 'tags.delete'],
  'Form Configurations': ['configs.view', 'configs.create', 'configs.edit', 'configs.delete'],
  'Team Users': ['users.view', 'users.invite', 'users.edit', 'users.delete', 'users.manage_roles'],
  Settings: ['settings.view', 'settings.edit'],
  'Audit Log': ['audit.view', 'audit.export'],
  'Roles & Permissions': ['roles.view', 'roles.create', 'roles.edit', 'roles.delete']
};
export const allPermissions: Permission[] = Object.values(permissionCategories).flat() as Permission[];
// ── Session persistence ──
const SESSION_KEY = 'configconsole_user_email';
function getStoredEmail(): string | null {
  try {
    return localStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}
function setStoredEmail(email: string) {
  try {
    localStorage.setItem(SESSION_KEY, email);
  } catch {}
}
function clearStoredEmail() {
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {}
}
// ── Workspace type ──
export interface Workspace {
  id: string;
  creatorId: string;
  name: string;
  region: string;
  subRegions: string[];
  logoUrl?: string;
  primaryColor?: string;
}
function teamToWorkspace(team: TeamRecord): Workspace {
  return {
    id: team.Team_ID,
    creatorId: team.ID,
    name: team.Team_Name,
    region: team.Team_Name,
    subRegions: (team.Team_Regions || []).map(r => r.Region_Name),
    logoUrl: team.Logo_Url || '',
    primaryColor: team.Primary_Color || '#2563eb'
  };
}
// ── App State ──
export type AppState = 'loading' | 'onboarding' | 'login' | 'ready' | 'unauthorized' | 'error';
export interface LoginState {
  email: string | null;
  needsPin: boolean;
  needsFullLogin: boolean;
}
// ── Context Type ──
interface WorkspaceContextType {
  // App state
  appState: AppState;
  loginState: LoginState | null;
  loading: boolean;
  error: string | null;
  // Core scope
  currentWorkspace: Workspace;
  currentSubRegion: string | null;
  setCurrentWorkspace: (workspace: Workspace) => void;
  setCurrentSubRegion: (subRegion: string | null) => void;
  workspaces: Workspace[];
  // Scope key — changes on every workspace/subRegion switch to force page remount
  scopeKey: number;
  // Current user
  currentUser: UserRecord & {
    role: string;
    name: string;
    email: string;
    id: string;
  };
  // Roles
  roles: RoleRecord[];
  // Permission helpers
  hasPermission: (permission: Permission) => boolean;
  getUserPermissions: (roleName?: string) => Permission[];
  // Session & lifecycle
  identifyUser: (email: string) => Promise<'not-found' | 'suspended' | 'needs-password' | 'has-password'>;
  verifyUserPassword: (email: string, password: string) => Promise<boolean>;
  setupUserPassword: (email: string, password: string) => Promise<boolean>;
  verifyQuickPin: (pin: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPasswordWithToken: (email: string, token: string, newPassword: string) => Promise<boolean>;
  completeOnboarding: () => Promise<void>;
  logout: () => void;
  refreshTeams: () => Promise<void>;
  refreshRoles: () => Promise<void>;
  getAdminContacts: () => {
    name: string;
    email: string;
    phone: string;
    role: string;
  }[];
  // ── Backward-compatible stubs ──
  forms: any[];
  templates: any[];
  submissions: any[];
  subscriptions: any[];
  contacts: any[];
  confirmedContacts: any[];
  users: any[];
  allUsers: any[];
  formConfigs: any[];
  newsletters: any[];
  campaigns: any[];
  campaignTemplates: any[];
  auditEvents: any[];
  tags: any[];
  addSubRegion: (s: string) => void;
  removeSubRegion: (s: string) => void;
  addContactCategory: (c: string) => void;
  removeContactCategory: (c: string) => void;
  createWorkspace: (w: any) => void;
  createUser: (u: any) => void;
  updateUserStatus: (id: number, status: string) => void;
  updateUserRole: (id: number, roleId: number) => void;
  deleteUser: (id: number) => void;
  saveFormConfig: (c: any) => void;
  deleteFormConfig: (id: number) => void;
  createNewsletter: (n: any) => void;
  updateNewsletterIntake: (id: number, status: string, msg?: string) => void;
  createCampaign: (c: any) => void;
  createCampaignTemplate: (t: any) => void;
  createTag: (t: any) => void;
  deleteTag: (id: number) => void;
  createContact: (c: any) => void;
  updateContact: (id: number, u: any) => void;
  deleteContact: (id: number) => void;
  getContactsByCategory: (cat: string) => any[];
  getConfirmedContactsByList: (id: number) => any[];
  getSubmissionsByTag: (tag: string) => any[];
  getSubmissionsByMailingList: (id: number) => any[];
  getSubscriptionsByMailingList: (id: number) => any[];
  getUnconfiguredForms: () => any[];
  getUnassignedForms: () => any[];
  duplicateForm: (id: number) => void;
  duplicateNewsletter: (id: number) => void;
  duplicateTemplate: (id: number) => void;
  logAuditEvent: (...args: any[]) => void;
  createRole: (r: any) => void;
  updateRole: (id: number, u: any) => void;
  deleteRole: (id: number) => void;
  getRoleById: (id: number) => any;
}
const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);
const FALLBACK_WORKSPACE: Workspace = {
  id: 'loading',
  creatorId: '',
  name: 'Loading...',
  region: '',
  subRegions: [],
  status: 'Active'
};
const FALLBACK_USER: UserRecord = {
  ID: '',
  User_Name: 'Loading...',
  Email: '',
  Role: 'Viewer',
  Team: '',
  Status: 'Active',
  Invited_By: ''
};
export function WorkspaceProvider({
  children
}: {
  children: ReactNode;
}) {
  const [appState, setAppState] = useState<AppState>('loading');
  const [loginState, setLoginState] = useState<LoginState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [currentWorkspace, _setCurrentWorkspace] = useState<Workspace>(FALLBACK_WORKSPACE);
  const [currentSubRegion, _setCurrentSubRegion] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<UserRecord>(FALLBACK_USER);
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [allUsersCache, setAllUsersCache] = useState<UserRecord[]>([]);
  // Scope key — incremented on user-initiated workspace/subRegion switches
  // Used as a React key to force full page remount
  const [scopeKey, setScopeKey] = useState(0);
  // Wrapped setters for user-initiated switches (increment scopeKey)
  const setCurrentWorkspace = useCallback((workspace: Workspace) => {
    _setCurrentWorkspace(workspace);
    setScopeKey(k => k + 1);
  }, []);
  const setCurrentSubRegion = useCallback((subRegion: string | null) => {
    _setCurrentSubRegion(subRegion);
    setScopeKey(k => k + 1);
  }, []);
  // ── Load initial data ──
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAppState('loading');
    try {
      const [teamsResult, usersResult, rolesResult] = await Promise.all([teamsApi.list(), usersApi.list(), rolesApi.list()]);
      const teamsData = teamsResult;
      const usersData = usersResult;
      const rolesData = rolesResult;
      setAllUsersCache(usersData);
      setRoles(rolesData);
      const ws = teamsData.map(teamToWorkspace);
      setWorkspaces(ws);
      if (usersData.length === 0) {
        setAppState('onboarding');
        setLoading(false);
        return;
      }
      // Check session
      const session = authSession.getSessionState();
      if (session.email) {
        const matchedUser = usersData.find(u => u.Email.toLowerCase() === session.email?.toLowerCase() && u.Status !== 'Suspended');
        if (matchedUser) {
          if (session.isValid && !session.needsFullLogin && !session.needsPin) {
            // Valid active session
            setCurrentUser(matchedUser);
            if (ws.length > 0) {
              const userTeam = ws.find(w => w.name === matchedUser.Team);
              _setCurrentWorkspace(userTeam || ws[0]);
            }
            authSession.updateActivity();
            setAppState('ready');
            setLoading(false);
            return;
          } else {
            // Needs PIN or full login
            setLoginState({
              email: session.email,
              needsPin: session.needsPin,
              needsFullLogin: session.needsFullLogin
            });
            setAppState('login');
            setLoading(false);
            return;
          }
        } else {
          authSession.clearSession();
        }
      }
      setLoginState(null);
      setAppState('login');
      setLoading(false);
    } catch (err) {
      console.error('Failed to load workspace data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      setAppState('error');
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);
  // Update activity every minute if logged in (separate effect)
  useEffect(() => {
    const interval = setInterval(() => {
      if (authSession.getSessionState().isValid) {
        authSession.updateActivity();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  // ── Identify user (login step 1) ──
  const identifyUser = useCallback(async (email: string): Promise<'not-found' | 'suspended' | 'needs-password' | 'has-password'> => {
    let users = allUsersCache;
    if (users.length === 0) {
      users = await usersApi.list();
      setAllUsersCache(users);
    }
    const matched = users.find(u => u.Email.toLowerCase() === email.toLowerCase());
    if (!matched) return 'not-found';
    if (matched.Status === 'Suspended') return 'suspended';
    if (matched.Password_Set && matched.Password_Hash) {
      return 'has-password';
    } else {
      return 'needs-password';
    }
  }, [allUsersCache]);
  // ── Verify Password (login step 2) ──
  const verifyUserPassword = useCallback(async (email: string, password: string): Promise<boolean> => {
    const matched = allUsersCache.find(u => u.Email.toLowerCase() === email.toLowerCase() && u.Status !== 'Suspended');
    if (!matched || !matched.Password_Hash || !matched.Password_Salt) return false;
    const isValid = await verifyPassword(password, matched.Password_Hash, matched.Password_Salt);
    if (isValid) {
      const token = generateToken();
      authSession.startSession(email, token);
      // Update last login
      try {
        await usersApi.update(matched.ID, {
          Last_Full_Login: new Date().toISOString(),
          Session_Token: token
        });
      } catch (e) {
        console.error('Failed to update last login', e);
      }
      setCurrentUser(matched);
      if (workspaces.length > 0) {
        const userTeam = workspaces.find(w => w.name === matched.Team);
        _setCurrentWorkspace(userTeam || workspaces[0]);
      }
      setAppState('ready');
      return true;
    }
    return false;
  }, [allUsersCache, workspaces]);
  // ── Setup Password (first time login) ──
  const setupUserPassword = useCallback(async (email: string, password: string): Promise<boolean> => {
    const matched = allUsersCache.find(u => u.Email.toLowerCase() === email.toLowerCase() && u.Status !== 'Suspended');
    if (!matched) return false;
    try {
      const salt = generateSalt();
      const hash = await hashPassword(password, salt);
      const token = generateToken();
      await usersApi.update(matched.ID, {
        Password_Hash: hash,
        Password_Salt: salt,
        Password_Set: true,
        Last_Full_Login: new Date().toISOString(),
        Session_Token: token
      });
      authSession.startSession(email, token);
      setCurrentUser({
        ...matched,
        Password_Set: true
      });
      if (workspaces.length > 0) {
        const userTeam = workspaces.find(w => w.name === matched.Team);
        _setCurrentWorkspace(userTeam || workspaces[0]);
      }
      setAppState('ready');
      return true;
    } catch (e) {
      console.error('Failed to setup password', e);
      return false;
    }
  }, [allUsersCache, workspaces]);
  // ── Verify Quick PIN ──
  const verifyQuickPin = useCallback(async (pin: string): Promise<boolean> => {
    if (!loginState?.email) return false;
    const matched = allUsersCache.find(u => u.Email.toLowerCase() === loginState.email?.toLowerCase() && u.Status !== 'Suspended');
    if (!matched) return false;
    // If no PIN set, they must use full password
    if (!matched.Quick_PIN_Hash || !matched.Quick_PIN_Salt) return false;
    const isValid = await verifyPassword(pin, matched.Quick_PIN_Hash, matched.Quick_PIN_Salt);
    if (isValid) {
      // Refresh session activity
      authSession.updateActivity();
      setCurrentUser(matched);
      if (workspaces.length > 0) {
        const userTeam = workspaces.find(w => w.name === matched.Team);
        _setCurrentWorkspace(userTeam || workspaces[0]);
      }
      setAppState('ready');
      return true;
    }
    return false;
  }, [allUsersCache, workspaces, loginState]);
  // ── Request Password Reset ──
  const requestPasswordReset = useCallback(async (email: string): Promise<boolean> => {
    const matched = allUsersCache.find(u => u.Email.toLowerCase() === email.toLowerCase() && u.Status !== 'Suspended');
    if (!matched) return false;
    try {
      // Generate a 6-digit code
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      // Expiry 24 hours from now
      const expires = new Date();
      expires.setHours(expires.getHours() + 24);
      await usersApi.update(matched.ID, {
        Reset_Token: code,
        Reset_Token_Expires: expires.toISOString()
      });
      return true;
    } catch (e) {
      console.error('Failed to request reset', e);
      return false;
    }
  }, [allUsersCache]);
  // ── Reset Password with Token ──
  const resetPasswordWithToken = useCallback(async (email: string, token: string, newPassword: string): Promise<boolean> => {
    const matched = allUsersCache.find(u => u.Email.toLowerCase() === email.toLowerCase() && u.Status !== 'Suspended');
    if (!matched || !matched.Reset_Token || matched.Reset_Token !== token) return false;
    // Check expiry
    if (matched.Reset_Token_Expires && new Date(matched.Reset_Token_Expires) < new Date()) {
      return false;
    }
    try {
      const salt = generateSalt();
      const hash = await hashPassword(newPassword, salt);
      await usersApi.update(matched.ID, {
        Password_Hash: hash,
        Password_Salt: salt,
        Password_Set: true,
        Reset_Token: '',
        Reset_Token_Expires: ''
      });
      return true;
    } catch (e) {
      console.error('Failed to reset password', e);
      return false;
    }
  }, [allUsersCache]);
  // ── Complete onboarding ──
  const completeOnboarding = useCallback(async () => {
    // Reload everything from Creator
    await loadInitialData();
  }, [loadInitialData]);
  // ── Logout ──
  const logout = useCallback(() => {
    authSession.clearSession();
    setCurrentUser(FALLBACK_USER);
    _setCurrentWorkspace(FALLBACK_WORKSPACE);
    _setCurrentSubRegion(null);
    setLoginState(null);
    setAppState('login');
  }, []);
  // ── Refresh helpers ──
  const refreshTeams = useCallback(async () => {
    try {
      const teamsData = await teamsApi.list();
      const ws = teamsData.map(teamToWorkspace);
      setWorkspaces(ws);
      const updated = ws.find(w => w.id === currentWorkspace.id);
      if (updated) _setCurrentWorkspace(updated);
    } catch (err) {
      console.error('Failed to refresh teams:', err);
    }
  }, [currentWorkspace.id]);
  const refreshRoles = useCallback(async () => {
    try {
      const rolesData = await rolesApi.list();
      setRoles(rolesData);
    } catch (err) {
      console.error('Failed to refresh roles:', err);
    }
  }, []);
  // ── Admin contacts (for login "not found" screen) ──
  const getAdminContacts = useCallback(() => {
    return allUsersCache.filter(u => (u.Role === 'Super Admin' || u.Role === 'Team Manager') && u.Status === 'Active').map(u => ({
      name: u.User_Name,
      email: u.Email,
      phone: u.Phone_Number || '',
      role: u.Role
    }));
  }, [allUsersCache]);
  // ── Permission helpers ──
  const getUserPermissions = useCallback((roleName?: string): Permission[] => {
    const name = roleName || currentUser.Role;
    if (name === 'Super Admin') return allPermissions;
    const role = roles.find(r => r.Role_Name === name);
    if (!role?.Role_Permissions) return [];
    return role.Role_Permissions.filter(Boolean).map(p => p.Permission_Key as Permission);
  }, [currentUser.Role, roles]);
  const hasPermission = useCallback((permission: Permission): boolean => {
    if (currentUser.Role === 'Super Admin') return true;
    const perms = getUserPermissions();
    return perms.includes(permission);
  }, [currentUser.Role, getUserPermissions]);
  // ── Backward-compatible stubs ──
  const noop = () => {};
  const emptyArr = () => [] as any[];
  const compatUser = {
    ...currentUser,
    role: currentUser.Role,
    name: currentUser.User_Name,
    email: currentUser.Email,
    id: currentUser.ID
  };
  return <WorkspaceContext.Provider value={{
    appState,
    loginState,
    loading,
    error,
    currentWorkspace,
    currentSubRegion,
    setCurrentWorkspace,
    setCurrentSubRegion,
    workspaces,
    scopeKey,
    currentUser: compatUser as any,
    roles,
    hasPermission,
    getUserPermissions,
    identifyUser,
    verifyUserPassword,
    setupUserPassword,
    verifyQuickPin,
    requestPasswordReset,
    resetPasswordWithToken,
    completeOnboarding,
    logout,
    refreshTeams,
    refreshRoles,
    getAdminContacts,
    forms: [],
    templates: [],
    submissions: [],
    subscriptions: [],
    contacts: [],
    confirmedContacts: [],
    users: [],
    allUsers: [],
    formConfigs: [],
    newsletters: [],
    campaigns: [],
    campaignTemplates: [],
    auditEvents: [],
    tags: [],
    addSubRegion: noop,
    removeSubRegion: noop,
    addContactCategory: noop,
    removeContactCategory: noop,
    createWorkspace: noop,
    createUser: noop,
    updateUserStatus: noop,
    updateUserRole: noop,
    deleteUser: noop,
    saveFormConfig: noop,
    deleteFormConfig: noop,
    createNewsletter: noop,
    updateNewsletterIntake: noop,
    createCampaign: noop,
    createCampaignTemplate: noop,
    createTag: noop,
    deleteTag: noop,
    createContact: noop,
    updateContact: noop,
    deleteContact: noop,
    getContactsByCategory: emptyArr,
    getConfirmedContactsByList: emptyArr,
    getSubmissionsByTag: emptyArr,
    getSubmissionsByMailingList: emptyArr,
    getSubscriptionsByMailingList: emptyArr,
    getUnconfiguredForms: emptyArr,
    getUnassignedForms: emptyArr,
    duplicateForm: noop,
    duplicateNewsletter: noop,
    duplicateTemplate: noop,
    logAuditEvent: noop,
    createRole: noop,
    updateRole: noop,
    deleteRole: noop,
    getRoleById: () => undefined
  }} data-id="element-2280">
      {children}
    </WorkspaceContext.Provider>;
}
export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}